import { Injectable } from '@angular/core';

import { AnnouncementType } from '../../banner/announcement-type.enum';
import { AddOptions, DeleteOptions } from '../../mru/models/mru-state-event.model';
import { IconFontType } from '../icon-font/icon-font-type.enum';
import { ErrorDialogErrorCategory, ErrorDialogErrorType } from '../models/error-dialog.model';
import { FileItem, FileType } from '../models/file-item.model';
import { FormEnum, FormType } from '../models/form.model';
import { toFileItemFromForm } from '../mru/mru-data-converter.utility';
import { MruFileFormat, MruFileItem } from '../mru/mru-file-item.model';
import { ErrorDialogService } from '../services/error-dialog.service';
import { EventService } from '../services/event.service';
import { FormsService } from '../services/forms.service';
import { FormsMoveResolveModel, SidePopupMenuFormsMoveModel, SidePopupMenuService, SidePopupMenuType } from '../services/side-popup-menu.service';
import { AngularEvents } from '../shared-context/angular-events';
import { ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { ContextService } from '../shared-context/context.service';
import { LocService } from '../shared-context/loc.service';
import { getDeferredPromise } from '../utilities/promise-deffered.utility';
import { MruItemStates } from './mru-item.component';

export interface MruEventEmitterToTrigger {
    key: string;
    props: any;
}
export interface MruAdditionalContextMenuItem {
    id: string;
    executeAction(event?: MouseEvent | KeyboardEvent): Promise<Array<MruEventEmitterToTrigger>>;
    disabled?: boolean;
    title: string;
    ariaLabel: string;
    icon?: IconFontType;
}

export interface MruContextMenuModifyItem {
    start: number;
    deleteCount: number;
    item: MruAdditionalContextMenuItem;
}

@Injectable()
export class MruItemService {

    constructor(
        private readonly locService: LocService,
        private readonly contextService: ContextService,
        private readonly sidePopupMenuService: SidePopupMenuService,
        private readonly formsService: FormsService,
        private readonly eventService: EventService,
        private readonly errorDialog: ErrorDialogService,
        private readonly clientStartupConfigService: ClientStartupConfigService
    ) { }

    public sendMruItemState(mruItemState: MruItemStates, item: MruFileItem) {
        this.eventService.broadcastEvent({
            name: AngularEvents.mruItemStateChange,
            args: [
                mruItemState,
                item
            ]
        });
    }

    public generateAdditionalActionsMenuItems(
        index: number,
        item: MruFileItem,
        isConsumer: boolean
    ): ReadonlyArray<MruAdditionalContextMenuItem> {
        const additionalActionsMenuItems: Array<MruAdditionalContextMenuItem> = [];
        const { fileType, format, isDeleted, isLocal } = item;

        switch (fileType) {
            case FileType.Forms:
            case FileType.FormsQuiz:

                const formEnumType = item.getFormEnumType();

                if (format === MruFileFormat.SharedForm) {
                    additionalActionsMenuItems.push({
                        id: 'formsSharedUnpin',
                        title: this.locService.RemoveFromSharedWithMe,
                        ariaLabel: this.locService.RemoveFromSharedWithMe,
                        icon: IconFontType.Unpin,
                        executeAction: () => Promise.resolve([
                            {
                                key: 'delete',
                                props: {
                                    index,
                                    item,
                                    deleteOption: DeleteOptions.UnpinnedShared
                                }
                            }
                        ]),
                        disabled: isLocal
                    });
                }

                if (format === MruFileFormat.Form &&
                    !isDeleted &&
                    item.ownerType !== FormType.Groups &&
                    formEnumType !== FormEnum.Poll && formEnumType !== FormEnum.Assignments &&
                    !isConsumer) {
                    additionalActionsMenuItems.push(this.generateFormsMoveAction(item, index));
                }

                if ((format === MruFileFormat.Form || format === MruFileFormat.SharedForm) &&
                    !isDeleted &&
                    formEnumType !== FormEnum.Poll && formEnumType !== FormEnum.Assignments) {
                    additionalActionsMenuItems.push(this.generateFormsCopyAction(item, isConsumer));
                }

                if (format === MruFileFormat.Form && isDeleted && !isLocal) {
                    additionalActionsMenuItems.push({
                        id: 'restore',
                        title: this.locService.RestoreActionMenuLabel,
                        ariaLabel: this.locService.RestoreActionMenuLabel,
                        icon: IconFontType.Restore,
                        executeAction: () => {
                            this.sendMruItemState(MruItemStates.Loading, item);

                            return this.attachFormsErrorHandler(
                                this.formsService.setFormsRecycleState(item.docId, item.ownerId, item.ownerType, false)
                                    .then(() => this.formsService.pushFormsAnnouncements(AnnouncementType.FormsRestored, item.fileName))
                                    .then(() => [{
                                        key: 'delete',
                                        props: {
                                            index,
                                            item,
                                            deleteOption: DeleteOptions.Restore
                                        }
                                    }]).finally(() => {
                                        this.sendMruItemState(MruItemStates.Resting, item);
                                    }));
                        },
                        disabled: false
                    });
                }

                if (format === MruFileFormat.Form && !isLocal) {
                    additionalActionsMenuItems.push({
                        id: 'delete',
                        title: this.locService.DeleteActionMenuLabel,
                        ariaLabel: this.locService.DeleteActionMenuLabel,
                        icon: IconFontType.Delete,
                        executeAction: () => {
                            this.sendMruItemState(MruItemStates.Loading, item);

                            let promise: Promise<any>;

                            if (isDeleted) {
                                // Hard delete form
                                promise = this.formsService.hardDeleteForm(item.docId, item.ownerId, item.ownerType)
                                    .then(() => this.formsService.pushFormsAnnouncements(AnnouncementType.FormsPermanentlyDeleted, item.fileName));
                            } else {
                                // Soft delete form (move to recycle bin)
                                promise = this.formsService.setFormsRecycleState(item.docId, item.ownerId, item.ownerType, true)
                                    .then(() => this.formsService.pushFormsAnnouncements(AnnouncementType.FormsMoveToRecycleBin));
                            }

                            return this.attachFormsErrorHandler(promise.then(() => [{
                                key: 'delete',
                                props: {
                                    index,
                                    item,
                                    deleteOption: isDeleted ? DeleteOptions.Hard : DeleteOptions.Soft
                                }
                            }]).finally(() => {
                                this.sendMruItemState(MruItemStates.Resting, item);
                            }));
                        },
                        disabled: false
                    });
                }
                break;

            default:
                break;
        }

        return additionalActionsMenuItems;
    }

    public updateActionItemsOnInflated(
        index: number,
        item: MruFileItem,
        isConsumer: boolean
    ): ReadonlyArray<MruContextMenuModifyItem> {
        const items: Array<MruContextMenuModifyItem> = [];

        switch (item.fileType) {
            case FileType.Forms:
            case FileType.FormsQuiz:

                if (item.format === MruFileFormat.Recent
                    && item.getFormEnumType() !== FormEnum.Poll
                    && item.getFormEnumType() !== FormEnum.Assignments
                    && item.metaData?.formsResponseCount !== undefined
                    && item.metaData?.formsResponseCount !== null) {

                    if (item.ownerType !== FormType.Groups && !isConsumer) {
                        items.push({
                            start: 1,
                            deleteCount: 0,
                            item: this.generateFormsMoveAction(item, index)
                        });
                    }
                    items.push({
                        start: 1,
                        deleteCount: 0,
                        item: this.generateFormsCopyAction(item, isConsumer)
                    });
                }

                break;
            default:
                break;
        }

        return items;
    }

    // Forms actions:
    private generateFormsCopyAction(item: MruFileItem, isConsumer: boolean): MruAdditionalContextMenuItem {
        return {
            id: 'copy',
            title: this.locService.CopyActionMenuLabel,
            ariaLabel: this.locService.CopyActionMenuLabel,
            icon: IconFontType.Copy,
            executeAction: () => {
                this.sendMruItemState(MruItemStates.Loading, item);

                let newOwnerId: string;
                const newOwnerType = item.ownerType;
                if (item.ownerType === FormType.Users) {
                    if (isConsumer) {
                        // MSA user has no shared feature, so using ownerId is good.
                        newOwnerId = item.ownerId;
                    } else {
                        // For AAD user, objectId is userId.
                        // ownerId can be different if it is shared-with-me form.
                        newOwnerId = this.clientStartupConfigService.config.objectId;
                    }
                } else if (item.ownerType === FormType.Groups) {
                    newOwnerId = item.ownerId;
                }

                return this.attachFormsErrorHandler(
                    this.formsService
                        .postCopyForm(
                            item.docId,
                            item.title,
                            item.ownerId,
                            newOwnerId,
                            item.ownerType,
                            newOwnerType
                        )
                        .then(form => {
                            this.formsService.setFormsCopySuccessAnnouncement(
                                form.title,
                                newOwnerType,
                                item.format !== MruFileFormat.Form, // If in All My Forms page, do not show the hint link.
                                newOwnerId,
                                item.createdBy
                            );

                            return [{
                                key: 'add',
                                props: {
                                    addOption: AddOptions.Copy,
                                    addedItem: toFileItemFromForm(
                                        form,
                                        `${FileItem.fileTypeNameFromType(FileType.Forms)}_item_${form.id}`,
                                        `${FileItem.fileTypeNameFromType(FileType.Forms)}Impression`,
                                        this.contextService.isRtl,
                                        this.clientStartupConfigService.config.authType,
                                        false,
                                        null,
                                        item.ownerType
                                    )
                                }
                            }];
                        }).finally(() => {
                            this.sendMruItemState(MruItemStates.Resting, item);
                        })
                );
            },
            disabled: item.isLocal
        };
    }

    private generateFormsMoveAction(item: MruFileItem, index: number): MruAdditionalContextMenuItem {
        return {
            id: 'move',
            title: this.locService.MoveActionMenuLabel,
            ariaLabel: this.locService.MoveActionMenuLabel,
            icon: IconFontType.Move,
            executeAction: () => {
                const [moveModelPromise, resolveMoveModel] = getDeferredPromise<FormsMoveResolveModel>();
                const sideMenuModel: SidePopupMenuFormsMoveModel = {
                    isShown: true,
                    type: SidePopupMenuType.FormsMove,
                    movingFileItem: item,
                    index,
                    resolveMoveModel
                };
                this.sidePopupMenuService.sidePopupMenuEmitter.next(sideMenuModel);

                return moveModelPromise.then(value => {
                    const originItem: MruFileItem = item;
                    this.sendMruItemState(MruItemStates.Loading, originItem);
                    const groupId = value.moveTargetId;
                    const groupName = value.moveTargetName;

                    return this.attachFormsErrorHandler(
                        this.formsService
                            .postMoveForms(originItem.docId, originItem.ownerType, originItem.ownerId, FormType.Groups, groupId)
                            .then(newForm => {
                                this.formsService.setFormsMoveToGroupSuccessAnnouncement(originItem.fileName, groupId, groupName);

                                item.createdBy = groupName;
                                item.ownerType = FormType.Groups;
                                item.ownerId = groupId;
                                item.docId = newForm.id;
                                return [{
                                    key: 'delete',
                                    props: {
                                        index,
                                        item,
                                        deleteOption: DeleteOptions.Move
                                    }
                                },
                                {
                                    key: 'disableAction',
                                    props: {
                                        id: 'move'
                                    }
                                }];
                            })
                            .finally(() => {
                                this.sendMruItemState(MruItemStates.Resting, originItem);
                            })
                    );
                });
            },
            disabled: item.isLocal
        };
    }

    private attachFormsErrorHandler(pr: Promise<any>) {
        return pr.catch(error => {
            let errorType: ErrorDialogErrorType;
            const errorCode: string = error.httpResponse?.data?.error?.code;
            switch (errorCode) {
                case '6102':
                    errorType = ErrorDialogErrorType.AutoBlockedForm;
                    break;
                case '6100':
                    errorType = ErrorDialogErrorType.TakenDownForm;
                    break;
                default:
                    errorType = ErrorDialogErrorType.Generic;
                    break;
            }

            this.errorDialog.showErrorDialog({
                category: ErrorDialogErrorCategory.ActionMenu,
                error: errorType
            });

            throw error;
        });
    }
}
